import React, { FC, useEffect, useRef } from 'react';
import { useExerVision, AssessmentPhase, UnifiedHandKeypoints, ExecutableAssessment } from '@exerai/vision-engine-web';
import { PauseCircle, PlayCircle, SkipNextOutlined, SkipPreviousOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { ReplayCanvasInnerWrapper, SessionReplayCanvasWrapper } from '@/components/common/Pose/styles';
import { getReplayCanvasFit } from '@/components/common/Pose/utils';
import { ReplaySlider } from './ReplaySlider/ReplaySlider';
import { getDisplayedHandEdges, getDisplayedHandKeypoints } from './hands/utils';
import { SessionReplayCurrentFrame, SessionReplayHeader, SessionReplayPlayControls } from './styles';

interface HandsSessionReplayProps {
    poseDimensions: { width: number; height: number };
    handsLog: UnifiedHandKeypoints[][];
    assessmentDefinition: ExecutableAssessment;
}

const REPLAY_SCALE = 1;

export const HandsSessionReplay: FC<HandsSessionReplayProps> = ({ poseDimensions, handsLog, assessmentDefinition }) => {
    const { session, outputCanvasRef, draw, frame, setFrame, setHandsKeypoints, setHandsEdges, isPlaying, handlePlay } =
        useExerVision();
    const { phase, setPhase, setIsReplay, setAssessment } = session;

    const isDelayedRef = useRef(false);

    const DEFAULT_FPS = 30;

    const { displayWidth, aspectRatio } = getReplayCanvasFit(poseDimensions);

    useEffect(() => {
        setIsReplay(true);
        setAssessment(assessmentDefinition);
        handlePlay(true);
    }, [assessmentDefinition, handlePlay, setAssessment, setIsReplay]);

    useEffect(() => {
        if (isDelayedRef.current === true || !handsLog || handsLog.length === 0) {
            return;
        }

        if (frame >= handsLog.length) {
            setPhase(AssessmentPhase.COMPLETE);
            handlePlay(false);
        }
        if (handsLog[frame]) {
            setHandsKeypoints(
                handsLog[frame].map((hand) => {
                    return getDisplayedHandKeypoints(hand);
                }),
            );
            setHandsEdges(
                handsLog[frame].map((hand) => {
                    return getDisplayedHandEdges(hand);
                }),
            );
        }

        if (isPlaying) {
            if (DEFAULT_FPS) {
                isDelayedRef.current = true;
                setTimeout(() => {
                    isDelayedRef.current = false;
                    setFrame((f) => f + 1);
                }, 1000 / DEFAULT_FPS);
            } else {
                setFrame((prevFrame) => {
                    return prevFrame + 1;
                });
            }
        }
    }, [frame, handlePlay, isPlaying, phase, handsLog, setHandsEdges, setFrame, setHandsKeypoints, setPhase]);

    useEffect(() => {
        draw(REPLAY_SCALE, '#2dc1bb', '#2dc1bb');
    }, [draw, frame]);

    const handleSlider = (_, frame) => {
        setFrame(frame);
        handlePlay(false);
    };

    const handlePrevClick = () => {
        setFrame((c) => c - 1);
        handlePlay(false);
    };

    const handleNextClick = () => {
        setFrame((c) => c + 1);
        handlePlay(false);
    };

    return (
        <>
            <SessionReplayHeader>
                <SessionReplayCurrentFrame>
                    {handsLog && frame !== undefined ? `Frame: ${frame} / ${handsLog.length}` : 'Loading'}
                </SessionReplayCurrentFrame>
            </SessionReplayHeader>

            <SessionReplayCanvasWrapper width={'100%'} height={'100%'}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ReplayCanvasInnerWrapper $displayWidth={displayWidth} $aspectRatio={aspectRatio}>
                        <canvas
                            ref={outputCanvasRef}
                            width={poseDimensions.width / (1 / REPLAY_SCALE)}
                            height={poseDimensions.height / (1 / REPLAY_SCALE)}
                            style={{ display: 'block', width: '100%', height: '100%' }}
                        />
                    </ReplayCanvasInnerWrapper>
                </div>
            </SessionReplayCanvasWrapper>

            <SessionReplayPlayControls>
                <div>
                    <IconButton
                        aria-label="prev"
                        onClick={() => handlePrevClick()}
                        edge="end"
                        style={{ color: '#666666', fontSize: 36 }}
                    >
                        <SkipPreviousOutlined fontSize="inherit" />
                    </IconButton>

                    <IconButton
                        aria-label="play/pause"
                        onClick={() => handlePlay()}
                        edge="end"
                        style={{ color: '#666666', fontSize: 36 }}
                    >
                        {isPlaying ? <PauseCircle fontSize="inherit" /> : <PlayCircle fontSize="inherit" />}
                    </IconButton>

                    <IconButton
                        aria-label="prev"
                        onClick={() => handleNextClick()}
                        edge="end"
                        style={{ color: '#666666', fontSize: 36 }}
                    >
                        <SkipNextOutlined fontSize="inherit" />
                    </IconButton>
                </div>
            </SessionReplayPlayControls>
            <ReplaySlider phases={undefined} frame={frame} frameCount={handsLog?.length} handleSlider={handleSlider} />
        </>
    );
};
